import { queryOptions } from '@tanstack/react-query';

import apiClient from '@/constants/http';
import { Logger } from '@/utils/logging';

import type {
  StandbyPublicSettings,
  StandbyPublicShop,
  StandbyPublicShopData,
  StandbyPublicShopQueryKey,
} from './types';

const MAX_RETRIES = 2;

const SHOP_NOT_FOUND_CODE = 'ResourceNotFound';

/**
 * The API client rejects with the response BODY rather than an AxiosError, so
 * a missing shop is identified by the restify error code, not an HTTP status.
 */
export const isStandbyShopNotFoundError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'code' in error &&
  (error as { code?: unknown }).code === SHOP_NOT_FOUND_CODE;

const fetchStandbyPublicShop = async (
  shopIdOrRoute: string,
  signal?: AbortSignal,
): Promise<StandbyPublicShopData> => {
  try {
    const { data: shop } = await apiClient.get<StandbyPublicShop>(
      `/v1/shop/${encodeURIComponent(shopIdOrRoute)}/details`,
      { signal },
    );

    const { data: settings } = await apiClient.get<StandbyPublicSettings>(
      `/v1/standby-list/public/${shop.id}/settings`,
      { signal },
    );

    return { shop, settings };
  } catch (error) {
    Logger.error('Error fetching standby public shop', { error });

    throw error;
  }
};

export const standbyPublicShopQuery = (shopIdOrRoute?: string) =>
  queryOptions<
    StandbyPublicShopData,
    Error,
    StandbyPublicShopData,
    StandbyPublicShopQueryKey
  >({
    queryKey: ['standbyPublic', 'shop', shopIdOrRoute],
    queryFn: ({ signal }) => {
      if (!shopIdOrRoute) {
        throw new Error('Shop id or route is required');
      }

      return fetchStandbyPublicShop(shopIdOrRoute, signal);
    },
    enabled: !!shopIdOrRoute,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    // A missing shop is terminal — retrying it only delays the not-found screen.
    retry: (failureCount, error) =>
      !isStandbyShopNotFoundError(error) && failureCount < MAX_RETRIES,
  });
